/**
 * /weather/about — Detailed "About the Weather Almanac" page.
 *
 * Long-form editorial page that expands on the mission, geographic scope,
 * what data we record, why it matters, and how the community can contribute.
 * Linked from the Weather Almanac hero via "Explore »".
 */
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Sun, CloudRain, Snowflake, Wind, Thermometer,
  Sprout, Users, BarChart3, Camera, Globe2, Sparkles,
} from "lucide-react";

export default function WeatherAlmanacAbout() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="weather-about-page">
      <Link
        to="/weather"
        className="inline-flex items-center gap-2 text-sm hover:underline"
        style={{ color: "var(--wofy-primary)" }}
        data-testid="weather-about-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Weather Almanac
      </Link>

      {/* Hero */}
      <div className="mt-6">
        <div className="overline">Social Pulse · Weather Almanac</div>
        <h1
          className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 font-bold"
          style={{ color: "var(--wofy-primary)" }}
        >
          🌦️ Social Pulse Weather Almanac
        </h1>
        <p className="mt-3 text-xl sm:text-2xl font-display" style={{ color: "var(--wofy-text)" }}>
          Your Community. Your Weather. Your History.
        </p>
        <p className="mt-5 text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
          The Social Pulse Weather Almanac is a living record of your community's weather,
          created by the people who experience it every day. More than just a forecast, it
          documents local weather conditions, unusual events, seasonal changes, and long-term
          trends that become valuable for future generations.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/weather?report=1"
            className="wofy-btn-primary"
            data-testid="about-report-weather-btn"
          >
            🌦️ Report Local Weather <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/weather" className="wofy-btn-ghost" data-testid="about-view-almanac-btn">
            View the almanac
          </Link>
        </div>
      </div>

      {/* Mission */}
      <Section icon={Sparkles} title="Mission">
        <p className="text-base italic" style={{ color: "var(--wofy-text)" }}>
          Record yesterday. Understand today. Prepare for tomorrow.
        </p>
        <p className="mt-3 leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
          Unlike standard weather apps that only show current conditions and forecasts, the
          Social Pulse Weather Almanac stores and analyzes years of local weather history for
          every community.
        </p>
      </Section>

      {/* What We Need to Record */}
      <Section icon={Globe2} title="🌍 What We Need to Record">
        <List
          items={[
            "Daily temperature, humidity and air quality",
            "Rainfall, snowfall and hail",
            "High winds, storms and tornadoes",
            "Floods, flash floods and cloudbursts",
            "Heat waves and cold spells",
            "Fog, frost and ice conditions",
            "Lightning, wildfires and smoke",
            "Avalanches, landslides and other natural events",
            "Sunrise, sunset and seasonal observations",
            "Photos, videos and eyewitness reports from the community",
          ]}
        />
      </Section>

      {/* Why It Matters */}
      <Section icon={Sprout} title="🌱 Why It Matters to Humanity">
        <p className="leading-relaxed mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          Your weather reports help:
        </p>
        <List
          items={[
            "Farmers plan planting, irrigation and harvesting.",
            "Gardeners protect plants from frost, heat and pests.",
            "Residents prepare for severe weather.",
            "Travelers check local conditions before visiting.",
            "Schools and businesses make informed decisions.",
            "Researchers and historians preserve local climate records.",
            "Emergency responders understand weather impacts.",
            "Communities learn from past weather events and become more resilient.",
          ]}
        />
      </Section>

      {/* Powerful Insights */}
      <Section icon={BarChart3} title="📊 Powerful Insights">
        <p className="leading-relaxed mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          The Weather Almanac transforms daily observations into:
        </p>
        <List
          items={[
            "Weather history by day, month and year",
            "Climate trends and seasonal comparisons",
            "Rainfall and snowfall totals",
            "Extreme weather records",
            "Interactive maps, charts and timelines",
            "Community photo archives",
            "Farmer and gardener recommendations",
            "Local weather alerts and preparedness information",
          ]}
        />
      </Section>

      {/* Geographic Organization */}
      <Section icon={Globe2} title="Geographic Organization">
        <p className="leading-relaxed mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          The Weather Almanac should be available by:
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            "🌍 Country", "State / Province", "County / District", "City",
            "Town", "Village", "Neighborhood", "GPS Location",
            "Farm", "Vineyard", "Orchard",
          ].map((t) => (
            <span
              key={t}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs border"
              style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
            >
              {t}
            </span>
          ))}
        </div>
        <p className="mt-3 text-sm italic" style={{ color: "var(--wofy-text-soft)" }}>
          Every location has its own weather history.
        </p>
      </Section>

      {/* Daily Weather Record */}
      <Section icon={Thermometer} title="Daily Weather Record">
        <p className="leading-relaxed mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          For every location, automatically record:
        </p>
        <SubBlock title="Basic Weather">
          <List
            columns
            items={[
              "Date", "Sunrise", "Sunset", "Moonrise", "Moonset", "Moon Phase",
              "Temperature High", "Temperature Low", "Average Temperature",
              "Humidity", "Dew Point", "Air Pressure", "Visibility", "UV Index",
              "Air Quality Index", "Wind Speed", "Wind Direction", "Wind Gust",
              "Rainfall", "Snowfall", "Snow Depth", "Ice Accumulation",
              "Soil Temperature", "Soil Moisture (where available)",
            ]}
          />
        </SubBlock>
      </Section>

      {/* Weather Events */}
      <Section icon={CloudRain} title="Weather Events">
        <p className="leading-relaxed mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          Record every significant weather event.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {[
            "☀ Heat Wave", "🔥 Extreme Heat", "🌧 Heavy Rain", "🌦 Light Rain",
            "⛈ Thunderstorm", "⚡ Lightning", "🌪 Tornado", "🌀 Cyclone",
            "🌀 Hurricane", "💨 High Winds", "🌬 Wind Storm", "🌫 Dense Fog",
            "🌨 Snowstorm", "❄ Blizzard", "🌨 Hail", "🧊 Ice Storm",
            "🏔 Avalanche", "🌊 Flood", "🌊 Flash Flood", "🏞 Landslide",
            "☁ Cloud Burst", "🌋 Volcanic Ash", "🔥 Wildfire Smoke", "🔥 Forest Fire",
            "🌪 Dust Storm", "🌫 Smog",
          ].map((t) => (
            <div
              key={t}
              className="rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              {t}
            </div>
          ))}
        </div>
      </Section>

      {/* Community Reports */}
      <Section icon={Camera} title="Community Weather Reports">
        <p className="leading-relaxed mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          Residents can submit:
        </p>
        <List
          columns
          items={[
            "Photos", "Videos", "Rainfall measurements", "Snow depth",
            "Wind damage", "Flooding", "Fallen trees", "Crop damage",
            "Road closures", "Power outages", "Hail size", "Ice accumulation",
          ]}
        />
        <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          These reports can be verified by nearby users and displayed on a live community map.
        </p>
      </Section>

      {/* Weather Calendar */}
      <Section icon={Sun} title="Weather Calendar">
        <p className="leading-relaxed mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          Instead of just numbers, display a color-coded calendar.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { color: "#22c55e", label: "🟢 Pleasant" },
            { color: "#fbbf24", label: "🟡 Warm" },
            { color: "#f97316", label: "🟠 Hot" },
            { color: "#dc2626", label: "🔴 Extreme Heat" },
            { color: "#3b82f6", label: "🔵 Rain" },
            { color: "#e5e7eb", label: "⚪ Snow" },
            { color: "#111827", label: "⚫ Storm" },
            { color: "#7c3aed", label: "🟣 Severe Weather" },
          ].map((c) => (
            <div
              key={c.label}
              className="rounded-lg border px-3 py-2 text-sm flex items-center gap-2"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <span className="inline-block w-3 h-3 rounded" style={{ background: c.color }} />
              {c.label}
            </div>
          ))}
        </div>
        <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          Users can quickly identify patterns over months and years.
        </p>
      </Section>

      {/* Farmer's Dashboard */}
      <Section icon={Sprout} title="Farmer's Dashboard">
        <p className="leading-relaxed mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          This is where the Weather Almanac becomes extremely valuable. Daily farming
          recommendations include:
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <SubBlock title="Planting">
            <List items={["Ideal planting days", "Too wet to plant", "Soil too cold", "Frost risk", "Wind warnings"]} />
          </SubBlock>
          <SubBlock title="Harvest">
            <List items={["Best harvest days", "Rain expected", "High humidity warning", "Strong wind warning", "Heat stress warning"]} />
          </SubBlock>
          <SubBlock title="Irrigation">
            <List items={["Recommended irrigation today", "Rain expected tomorrow", "Soil moisture", "Evaporation estimate", "Water savings recommendations"]} />
          </SubBlock>
          <SubBlock title="Fertilizer">
            <List items={["Safe spraying conditions", "High wind warning", "Rain after spraying", "Temperature warning"]} />
          </SubBlock>
          <SubBlock title="Livestock">
            <List items={["Heat stress alerts", "Cold stress alerts", "Water consumption estimates", "Shelter recommendations", "Mosquito activity"]} />
          </SubBlock>
          <SubBlock title="Gardening">
            <List items={["Water today?", "Fertilize?", "Prune?", "Harvest?", "Mulch?", "Expected frost?", "Best planting days", "Moon gardening suggestions"]} />
          </SubBlock>
        </div>
      </Section>

      {/* Emergency Alerts */}
      <Section icon={Wind} title="Emergency Alerts">
        <p className="leading-relaxed mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          Residents receive alerts for:
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {[
            "Flood Watch", "Flood Warning", "Heat Advisory", "Red Flag Warning",
            "Hurricane", "Tornado", "High Wind", "Avalanche",
            "Road Ice", "Heavy Snow", "Wildfire Smoke", "Air Quality",
          ].map((t) => (
            <div
              key={t}
              className="rounded-lg border px-3 py-2 text-sm text-center"
              style={{ borderColor: "#fca5a5", background: "#fee2e2", color: "#7f1d1d" }}
            >
              {t}
            </div>
          ))}
        </div>
      </Section>

      {/* AI Assistant */}
      <Section icon={Sparkles} title="AI Smart Weather Assistant">
        <p className="leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
          An AI assistant can combine forecasts, historical data, and user context to provide
          practical guidance:
        </p>
        <ul className="mt-3 space-y-2 text-sm">
          {[
            "Delay planting tomatoes by two days due to overnight frost risk.",
            "Harvest strawberries today because heavy rain is expected tomorrow.",
            "Soil moisture is sufficient; irrigation can be skipped.",
            "High pollen and poor air quality today—limit outdoor activity if sensitive.",
            "This is the third consecutive week below normal rainfall; monitor irrigation needs.",
          ].map((q) => (
            <li key={q} className="italic pl-3 border-l-2" style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-text-soft)" }}>
              "{q}"
            </li>
          ))}
        </ul>
      </Section>

      {/* Business Uses */}
      <Section icon={BarChart3} title="Business Uses">
        <p className="leading-relaxed mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          Businesses can use the Weather Almanac to plan staffing, inventory, and operations:
        </p>
        <List
          items={[
            "Restaurants can forecast outdoor dining demand.",
            "Grocery stores can stock ahead of storms or heat waves.",
            "Construction companies can plan work around weather windows.",
            "Delivery services can anticipate delays.",
            "Event organizers can schedule around historical weather patterns.",
          ]}
        />
      </Section>

      {/* Integration with Social Pulse */}
      <Section icon={Users} title="Integration with Social Pulse">
        <p className="leading-relaxed mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          The Weather Almanac is tightly integrated with the broader Social Pulse ecosystem:
        </p>
        <div className="space-y-2 text-sm">
          {[
            ["Marketplace", "Farmers can adjust produce availability based on harvest conditions and weather-related supply."],
            ["Events", "Outdoor event organizers receive weather risk assessments and historical climate insights."],
            ["Travel", "Travelers can view destination weather, seasonal patterns, and road condition reports."],
            ["Civic Reporting", "Citizen reports of flooding, fallen trees, icy roads, and storm damage automatically enrich the weather event map."],
            ["Real Estate", "Property listings can include long-term climate indicators such as flood history, average rainfall, and wildfire risk."],
            ["Food Pulse", "Weather-driven crop forecasts can help predict food availability, seasonal shifts, and potential shortages."],
          ].map(([k, v]) => (
            <div
              key={k}
              className="rounded-lg border p-3"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <div className="font-medium">{k}</div>
              <div className="mt-1" style={{ color: "var(--wofy-text-soft)" }}>{v}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Join CTA */}
      <Section icon={Users} title="🤝 Join the Community Weather Team">
        <p className="leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
          Every observation matters. Whether you report sunshine, a passing shower, heavy
          snowfall, dense fog or a damaging storm, you're helping build a valuable community
          resource that benefits everyone.
        </p>
        <p className="mt-3 leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
          Take a photo. Record an observation. Share your experience. Help your community
          stay informed, prepared and connected.
        </p>
        <p className="mt-3 leading-relaxed font-medium" style={{ color: "var(--wofy-text)" }}>
          Together, let's create the world's largest community-powered Weather Almanac—one
          day, one observation and one neighborhood at a time.
        </p>
        <div className="mt-6">
          <Link
            to="/weather#community-reports"
            className="wofy-btn-primary"
            data-testid="about-cta-report-btn"
          >
            🌦️ Report Local Weather <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Section>
    </div>
  );
}

// ---------- Building blocks ----------

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mt-10">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />}
        <h2 className="font-display text-2xl sm:text-3xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function SubBlock({ title, children }) {
  return (
    <div className="rounded-lg border p-4" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="text-xs uppercase tracking-widest font-mono mb-2" style={{ color: "var(--wofy-text-soft)" }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function List({ items, columns = false }) {
  return (
    <ul
      className={
        "text-sm space-y-1 " +
        (columns ? "grid grid-cols-1 sm:grid-cols-2 gap-x-6" : "")
      }
      style={{ color: "var(--wofy-text-soft)" }}
    >
      {items.map((it) => (
        <li key={it} className="pl-4 relative">
          <span
            className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full"
            style={{ background: "var(--wofy-primary)" }}
          />
          {it}
        </li>
      ))}
    </ul>
  );
}
